import { CheckIcon, EnvelopeOpenIcon, LockClosedIcon, TrashIcon } from "@radix-ui/react-icons";
import { useState } from "react";
import toast from "react-hot-toast";
import { useMutation, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ConfirmAlertDialog } from "../../components/common/ConfirmAlertDialog";
import { Lists } from "../../components/common/Lists";
import TableComponent from "../../components/common/Tables";
import { useAppDispatch } from "../../hooks/hooks";
import { useGetStudentDocumentsQuery } from "../../hooks/useGetStudentDocumentsQuery";
import { setPdfPreviewType } from "../../redux/slices/report/report";
import { setStudentCertificateData, setStudentData } from "../../redux/slices/school/student";
import { RootState } from "../../redux/store";
import { routes } from "../../routes";
import { updateStudentDocument } from "../../services/school/student/createStudent";
import { getStudentByCode, getStudentById } from "../../services/school/student/getStudentByCode";
import { getStudentsAssessments } from "../../services/school/student/getStudentsAssessments";
import { StudentAssessmentType } from "../../types/Assessment";
import { DocumentTypeEnum, getPrintTypeForStudentDoc, StateEnum, StudentDocumentType } from "../../types/Student";
import { Key_studentDocuments } from "../../utils/constants";
import { generateCertificateGrades } from "../../utils/gradeCalculator";
import { getSubjectsAprovedQuery } from "../../services/school/subject/getSubjects";
import { useLogoBase64 } from "../../hooks/useLogoBase64";
import { useExportDocument } from "../../hooks/useExportDocument";
import { numberinFull } from "../../utils/codes";
import { getUserById } from "../../services/user/getUsers";
import { useIES } from "../../hooks/useIES";


const columnNamesMap: { [key: string]: string } = {
  student: "Estudante",
  studentCode: "Código",
  docType: "Tipo de documento",
  state: "Estado",
  payment_status: "Estado de pagamento",
};

export const EmitStudentDocuments = () => {
  const { IES, isLoading } = useIES();
  const [currentPage, setCurrentPage] = useState(1);
  const userData = useSelector((state: RootState) => state.login.userData)
  const { logoBase64 } = useLogoBase64();
  const { exportDocumentPdf: exportDocument, downloadOnly } =
    useExportDocument(); 
     const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { data, status } = useGetStudentDocumentsQuery({ PageNumber: currentPage, PageSize: 400 });
  const [deleteItem, setDeleteItem] = useState<StudentDocumentType | null>(null);
  const [approveItem, setApproveItem] = useState<StudentDocumentType | null>(null);
  const [repproveItem, setRepproveItem] = useState<StudentDocumentType | null>(null);
  const mutationUpdate = useMutation(updateStudentDocument, {
    onSuccess: () => {
      queryClient.invalidateQueries(Key_studentDocuments);
      toast.success('Ação sucedida');
    },
    onError: () => {
      toast.error("Erro ao apagar documento.")
    }
  });
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  const handleClickCreate = (event: any): void => {
    navigate(routes.createStudentDocument)
  }
  const handlePreviewDocument = async (c: StudentDocumentType) => {
    // const docType = getPrintTypeForStudentDoc(c.docType);
    await loadPdfData(c);
    // dispatch(setPdfPreviewType(docType!))
    // navigate(routes.pdfPreview)
  };

  const loadPdfData = async (c: StudentDocumentType) => {
    if (c.docType === DocumentTypeEnum.CERTIFICADO || c.docType === DocumentTypeEnum.DECLARACAO) {
      let studentGrades = await getSubjectsAprovedQuery({ PageNumber: 1, PageSize: 1000, IdStudent: c.idStudent })
      const certificateGrades = studentGrades.items.map(i => {
        return {
          ...i,
          finalGrade: i?.grade ? Math.round(i.grade) : 0
        }
      });
      if (certificateGrades.length == 0) {
        toast.error("Sem notas para emissão do documento solicitado");
        return;
      }
      const studentData = await getStudentByCode(c.studentCode);
      const studentUser = await getUserById(studentData.idUser);

      const certificate = {
        grades: certificateGrades,
        student: studentData
      };
      const currentDate = new Date().toLocaleDateString("pt-PT");
      const orderedGrades = [...certificate.grades].sort((a, b) => {
          if (a.level !== b.level) {
            return a.level - b.level;
          }
          return a.semester - b.semester;
        });
      const data: any = {
        type:c.modelType ,
        outputFormat: "DOCX",
        idStudent: c.idStudent,
        idCourse: studentData.idCourse,
        data: {
          nome_completo: userData.Fullname,
          cargo: userData?.Role,
          ies: IES.NAME,
          documento_de_identificacao:studentUser?.bi,
          curso: studentData?.course,
          estudante: studentData?.fullname,
          // natural_de: studentData?.province,
          // nascido_em: studentUser.dateOfBirth,
          grades: orderedGrades.map(g => ({
            disciplina: g.subject,
            resultado: `${g.finalGrade} (${numberinFull(g.grade)} Valores)`,
            nivel_semestre: `${g.level}/${g.semester}`,
            ano_lectivo_disciplina: `${g.academicYear}`,
          })),
          mae: studentData?.mother,
          pai: studentData?.father,
          // data_emissao_documento: studentData?.dateDocumentEmmited,
          emitido: `${currentDate}`,
          local: IES.COUNTRY,
        },
        logoBase64: logoBase64 ?? '',
      }
      await exportDocument(data, c.modelType);
      return;
    }
    if (c.docType === DocumentTypeEnum.DIPLOMA || c.docType === DocumentTypeEnum.CREDENCIAL) {
      const studentData = await getStudentByCode(c.studentCode);
      dispatch(setStudentData(studentData))
    }
  }
  const handleDelete = (c: StudentDocumentType) => {
    if (c.state === StateEnum.INITIATE) {
      c.status = false;
      mutationUpdate.mutateAsync(c)
    }
  };
  const handleApprove = (c: StudentDocumentType) => {
    if (c.state !== StateEnum.INITIATE) {
      toast.success("Documento em estado irreversível");
      return;
    }
    c.approvedDate = new Date().toISOString();
    c.approvedBy = `${userData.Fullname} | ${userData.Role}`
    c.state = StateEnum.APPROVED;
    mutationUpdate.mutateAsync(c)
  };
  const handleReprove = (c: StudentDocumentType) => {
    if (c.state !== StateEnum.INITIATE) {
      toast.success("Documento em estado irreversível");
      return;
    }
    c.rejectedDate = new Date().toISOString();
    c.rejectedBy = `${userData.Fullname} | ${userData.Role}`
    c.state = StateEnum.REPPROVED;
    mutationUpdate.mutateAsync(c)
  };
  const openDeleteDialog = (s: StudentDocumentType) => {
    setDeleteItem(s);
  };
  const openApproveDialog = (s: StudentDocumentType) => {
    setApproveItem(s);
  };
  const openRepproveDialog = (s: StudentDocumentType) => {
    setRepproveItem(s);
  };
  const confirmDelete = async () => {
    if (deleteItem) {
      await handleDelete(deleteItem!)
    }
  };
  const confirmApprove = async () => {
    if (approveItem) {
      await handleApprove(approveItem!)
    }
  };
  const confirmRepprove = async () => {
    if (repproveItem) {
      await handleReprove(repproveItem!)
    }
  };

  const options = data?.items.map((c: StudentDocumentType) => [
    {
      label: 'Aprovar',
      onClick: () => openApproveDialog(c),
      icon: <CheckIcon />
    },
    {
      label: 'Reprovar',
      onClick: () => openRepproveDialog(c),
      icon: <LockClosedIcon />
    },
    {
      label: 'Pré-visualizar documento requerido',
      onClick: () => handlePreviewDocument(c),
      icon: <EnvelopeOpenIcon />
    },
    {
      label: 'Eliminar',
      onClick: () => openDeleteDialog(c),
      icon: <TrashIcon />
    },
  ]) || [];

  return (
    <>
      <Lists
        status={status}
        TableComponent={() =>
          <TableComponent
            columns={Object.keys(columnNamesMap)}
            items={data?.items.map((c: StudentDocumentType) => ({
              ...c,
              payment_status: c.isValid ? 'Pago' : 'Pendente de pagamento',
            })) || []}
            options={options}
            columnNamesMap={columnNamesMap}
            totalPages={data?.totalPages || 1}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          />
        }
        handleClickCreate={handleClickCreate}
      />
      <ConfirmAlertDialog
        open={!!deleteItem}
        onOpenChange={(open) => !open && setDeleteItem(null)}
        title="Eliminar documento"
        description="Esta operação é irreversível. O pedido de documento será eliminado."
        confirmLabel="Eliminar"
        confirmColor="red"
        item={deleteItem}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteItem(null)}
      />
      <ConfirmAlertDialog
        open={!!repproveItem}
        onOpenChange={(open) => !open && setRepproveItem(null)}
        title="Reprovar documento"
        description="Esta operação é irreversível. O pedido passará ao estado reprovado."
        confirmLabel="Reprovar"
        confirmColor="red"
        item={repproveItem}
        onConfirm={confirmRepprove}
        onCancel={() => setRepproveItem(null)}
      />
      <ConfirmAlertDialog
        open={!!approveItem}
        onOpenChange={(open) => !open && setApproveItem(null)}
        title="Aprovar documento"
        description="Esta operação é irreversível. O pedido passará ao estado aprovado."
        confirmLabel="Aprovar"
        confirmColor="green"
        item={approveItem}
        onConfirm={confirmApprove}
        onCancel={() => setApproveItem(null)}
      />
    </>
  );
}
